import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import { Box } from '@/shared/ui';
import { selectCreditCards, selectOnboardingUser, selectUserProfileInfo } from '@/store/slices/onboarding/selectors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CustomersController, CustomersStepTypeEnum, CustomersStepTypeEnumPaths, ICustomersInsuranceManualResponse, ICustomersInsuranceTestClaimPrescription, IPrescriptionCopayQuantity } from '@/shared/endpoints/customers';
import { toast } from 'react-toastify';
import { ERROR_MESSAGES } from '@/shared/constants/error-messages';
import { URLSearchParamsInit, useNavigate, useLocation } from 'react-router-dom';
import _, { cloneDeep } from 'lodash';
import { useTheme } from '@mui/material';
import { ROUTES } from '@/shared/constants/routes';
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import { GtmEvent } from '@/shared/types/enums';
import saleForceWebHookInstance from '@/services/saleforce';
import { withScrollRestoration } from '@/shared/hocs/withScrollRestoration';
import { _SplitLayout } from '@/modules/onboarding/components/split-layout';
import { completeOnboardingNotesRequest, cropBase64ImageWithLine } from './helpers';
import { convertedMedications, getPrescriptionsWithQuantites } from '@/shared/helpers/main';

import { isCopayRequestLimitReached, testClaim, updateTestClaimMetadataModel, isCopayRequestLimitReachedBasic } from '@/shared/helpers/test-claim';
import { findAllEligibleInsurance, getCopayEligiblePrescriptions } from '@/shared/helpers/eligibility';
import { usePrevious } from '@/shared/hooks/previous';

import { setNestedObjectValues, useFormik } from 'formik';
import { validationSchema } from '@/shared/components/payment-form/validations';
import { paymentFormInitialValues } from '@/shared/components/payment-form/constants';
import ReviewSubmit from './components/submit';
import ReviewPayment from './components/payment';
import ReviewMedicationList from './components/medication-list';
import ReviewMainInfo from './components/main-info';
import { store } from '@/store';
import { uploadSignature } from '../insurance/components/upload-form/helpers';
import { useScrollToFirstError } from './components/useScrollToFirstError';

interface Props {
    navigateToStep?: (step: CustomersStepTypeEnumPaths, index?: number, searchParams?: URLSearchParamsInit) => void;
}

type PaymentOption = 'now' | 'later';

const Review = ({ navigateToStep }: Props) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const sendDataToGTM = useGTMDispatch();
    const currentUser = useAppSelector(selectOnboardingUser);
    const existingCreditCards = useAppSelector(selectCreditCards);
    const [showCopay, setShowCopay] = useState(false);
    const [showCopayLoading, setShowCopayLoading] = useState(false);
    const [completeLoading, setCompleteLoading] = useState(false);
    const [claims, setClaims] = useState<Record<string, ICustomersInsuranceTestClaimPrescription>>({});
    const [eligibleInsurances, setEligibleInsurances] = useState<ICustomersInsuranceManualResponse[]>([]);
    const previousUser = usePrevious(currentUser);
    const userProfile = useAppSelector(selectUserProfileInfo);
    const [paymentOption, setPaymentOption] = useState<PaymentOption | null>(null);
    const [paymentMethodError, setPaymentMethodError] = useState(false);
    const scrollToFirstError = useScrollToFirstError();
    const [copayRequestsExhausted, setCopayRequestsExhausted] = useState(false);

    const initialVals = {
        ...paymentFormInitialValues,
        preferred: true,
        firstName: userProfile?.firstName || '',
        lastName: userProfile?.lastName || '',
    };

    const paymentForm = useFormik({
        initialValues: initialVals,
        validationSchema,
        onSubmit: _.noop, // handled by payment form
    });

    const getEligibleInsurances = useCallback(async () => {
        const isInitial = !previousUser && currentUser;

        if (!isInitial || !currentUser.userOnboarding) return;

        const result = await findAllEligibleInsurance(currentUser.insurances, currentUser.transferPrescriptions);

        result.newClaims.length && dispatch(CustomersController.updateMetadata(
            updateTestClaimMetadataModel(currentUser.userOnboarding, result.newClaims)
        ));

        setEligibleInsurances(result.eligibleInsurances);
    }, [currentUser, previousUser, dispatch]);

    useEffect(() => {
        getEligibleInsurances();
    }, [getEligibleInsurances]);

    const prescribables = useMemo(() => convertedMedications(currentUser!), [currentUser]);

    const triggerOnboardingCompletedSfWebhook = useCallback(async (email: string) => {
        try {
            await saleForceWebHookInstance.post({
                email,
                phone: '',
                activation: 'activation2'
            });
        } catch (error) {
            console.error('Error in SaleForceWebHook:', error);
        }
    }, []);

    const sendDataToGTMForPrescriptions = useCallback(() => {
        const otcCount =
            prescribables.includingMedbox.filter(item => item.otc).length +
            prescribables.packagedSeparately.filter(item => item.otc).length;
        const prescriptionCount = prescribables.includingMedbox.length + prescribables.packagedSeparately.length - otcCount;
        sendDataToGTM({
            event: GtmEvent.PRESCRIPTION_ADDED,
            userId: currentUser?.userProfile?.accountId,
            prescriptionCount,
            otcCount,
            final: true
        });
    }, [currentUser?.userProfile?.accountId, prescribables, sendDataToGTM]);

    const sendDataToGTMForOnboardingCompleted = useCallback(() => {
        sendDataToGTM({ event: GtmEvent.ONBOARDING_COMPLETED, userId: currentUser?.userProfile?.accountId });
    }, [currentUser?.userProfile?.accountId, sendDataToGTM]);

    const handleSubmit = useCallback(async (base64EncodedImage: string) => {
        if (completeLoading) return;

        const pendingPaymentForm = paymentOption === 'now' && !existingCreditCards?.length;
        if (pendingPaymentForm) {
            const errors = await paymentForm.validateForm();

            setPaymentMethodError(_.isEmpty(errors));

            paymentForm.setErrors(errors);
            paymentForm.setTouched(setNestedObjectValues(errors, true));
            scrollToFirstError();
            return;
        } else setPaymentMethodError(!paymentOption);

        if (!currentUser || (!paymentOption && !existingCreditCards.length)) return;

        setCompleteLoading(true);

        try {
            const croppedAndDatedSig = await cropBase64ImageWithLine(base64EncodedImage);
            if (!croppedAndDatedSig) {
                throw new Error('Signature is empty');
            }

            const uploadRes = await dispatch(
                uploadSignature(croppedAndDatedSig)
            );

            if (uploadRes.meta.requestStatus === 'rejected') {
                throw new Error('Upload failed');
            }

            const finishRes = await dispatch(CustomersController.onFinishOnboarding());

            if (finishRes.meta.requestStatus === 'rejected') {
                throw new Error('Onboarding finish failed');
            }

            completeOnboardingNotesRequest(currentUser);

            if (currentUser?.userOnboarding) {
                const payload = cloneDeep(currentUser.userOnboarding);
                payload.details = {
                    ...payload.details,
                    currentStep: CustomersStepTypeEnum.ThankYou,
                };

                await dispatch(CustomersController.updateMetadata(payload));

                sendDataToGTMForPrescriptions();
                sendDataToGTMForOnboardingCompleted();
                triggerOnboardingCompletedSfWebhook(currentUser?.email);

                navigate(ROUTES.THANK_YOU);
            }
        } catch (error) {
            toast.error(ERROR_MESSAGES.DEFAULT);
        } finally {
            setCompleteLoading(false);
        }
    }, [completeLoading, paymentOption, existingCreditCards.length, currentUser, paymentForm, scrollToFirstError, dispatch, sendDataToGTMForPrescriptions, sendDataToGTMForOnboardingCompleted, triggerOnboardingCompletedSfWebhook, navigate]);

    const handleShowCopays = async (quantitesMap?: Record<string, IPrescriptionCopayQuantity>) => {
        if (!currentUser?.userOnboarding?.details) return;

        setShowCopayLoading(true);

        try {
            const latestUserOnboarding = {
                ...store.getState().onboarding.user!.userOnboarding!,
            };

            const insurance = currentUser.insurances[0];
            const prescriptionsWithQuantites = getPrescriptionsWithQuantites(
                currentUser.transferPrescriptions,
                quantitesMap
            );

            const eligiblePrescriptions = getCopayEligiblePrescriptions(prescriptionsWithQuantites);
            const batchSize = 4;

            const claimMap: Record<string, ICustomersInsuranceTestClaimPrescription> = {};
            let totalUpdateCount = 0;
            let arrayOfUpdatedPrescriptions: ICustomersInsuranceTestClaimPrescription[] = [];
            for (let i = 0; i < eligiblePrescriptions.length; i += batchSize) {
                const batch = eligiblePrescriptions.slice(i, i + batchSize);

                const claimResult = await testClaim(
                    insurance,
                    batch,
                    null,
                    isCopayRequestLimitReached(latestUserOnboarding)
                );

                // Merge prescriptions into claimMap
                (claimResult.result?.prescriptions || []).forEach(presc => {
                    if (prescriptionsWithQuantites.some(item =>
                        +presc.medid === +item.medid &&
                        item.copayQuantity === presc.copayQuantity &&
                        item.copayDaysSupply === presc.copayDaysSupply)) {
                        claimMap[presc.medid] = presc;
                    }
                });
                // Accumulate update count
                totalUpdateCount += claimResult.updateCount || 0;
                if (claimResult.hasUpdate) {
                    arrayOfUpdatedPrescriptions = [...arrayOfUpdatedPrescriptions, ...claimResult.result.prescriptions || []];
                }
            }
            // Set final claims state
            setClaims(claimMap);
            setShowCopay(true);

            // Increment copay request count if needed
            if (totalUpdateCount) {
                const copayRequestCount = (latestUserOnboarding.details?.copayRequestCount || 0) + totalUpdateCount;
                latestUserOnboarding.details = {
                    ...latestUserOnboarding.details!,
                    copayRequestCount,
                };
            }
            if (isCopayRequestLimitReachedBasic(latestUserOnboarding.details!.copayRequestCount || 0)) {
                setCopayRequestsExhausted(true);
            }

            arrayOfUpdatedPrescriptions.length && dispatch(CustomersController.updateMetadata(
                updateTestClaimMetadataModel(latestUserOnboarding, [{ insuranceId: insurance.id, prescriptions: arrayOfUpdatedPrescriptions }])
            ));
        } finally {
            navigate({
                pathname: location.pathname,
                search: `?${new URLSearchParams({ copayTriggered: 'true' })}`,
            });
            setShowCopayLoading(false);
        }
    }

    const handleChangeQuantity = (medid: number, newQuantity: IPrescriptionCopayQuantity) => {
        const newQuantitesMap = { ...(currentUser?.userOnboarding?.details?.prescriptionCopayQuantities ?? {}) };
        newQuantitesMap[medid] = newQuantity;

        handleShowCopays(newQuantitesMap);
    }

    if (!currentUser) return null;

    return (
        <>
            <div className="mb-[2.1875rem] flex justify-center">
                <h2 className="review__heading text-center font-agrandir text-2xl md:text-3xl lg:text-left xl:text-5xl">
                    Let’s Review <span className="text-primary-700">Your MedBox</span>
                </h2>
            </div>
            <_SplitLayout
                customLeftClassNames={['!pl-0', 'xl-max:mb-[1rem]']}
                sideContent={
                    <Box
                        bgWhite
                        sx={{
                            gap: '28px',
                            padding: '30px !important',
                            boxShadow: '0px 4px 7.1px 0px #0000000F',
                            flex: '1 1 auto',
                            borderRadius: '20px',
                            maxWidth: '647px',

                            [theme.breakpoints.up('xl')]: {
                                width: 'calc(100% - 1.3125rem)',
                                marginRight: '1.3125rem'
                            },

                            [theme.breakpoints.down('xl')]: {
                                width: '100%',
                                maxWidth: '100%'
                            },

                            [theme.breakpoints.down('sm')]: {
                                padding: '20px !important'
                            },
                            '@media only screen and (min-width: 1441px)': {
                                maxWidth: '700px'
                            }
                        }}
                    >
                        <ReviewMainInfo
                            copayRequestsExhausted={copayRequestsExhausted}
                            claims={claims}
                            showCopayLoading={showCopayLoading}
                            onShowCopay={() => handleShowCopays(currentUser.userOnboarding?.details?.prescriptionCopayQuantities)}
                            eligibleInsurances={eligibleInsurances}
                            navigateToStep={navigateToStep}
                            prescribables={prescribables}
                        />

                        <ReviewMedicationList
                            showCopay={showCopay}
                            copayRequestsExhausted={copayRequestsExhausted}
                            claims={claims}
                            prescribables={prescribables}
                            navigateToStep={navigateToStep}
                            onQuantityChange={handleChangeQuantity}
                            eligibleInsurances={eligibleInsurances}
                        />

                        <ReviewPayment
                            paymentMethodError={paymentMethodError}
                            paymentOption={paymentOption}
                            setPaymentOption={setPaymentOption}
                            formikControl={paymentForm}
                        />
                    </Box>
                }
                customRightClassNames={['xl-max:w-full']}
                mainContent={<ReviewSubmit completeLoading={completeLoading} onSubmit={handleSubmit} />}
            />
        </>
    );
};

export default withScrollRestoration(Review);
